import React from 'react';

import { ADDRESS_VALIDATION_STATUSES, ADDRESS_VALIDATION_TYPE } from '~/components/Contacts/addressValidationStatuses';
import type {
  GbgAddressRetrieveValidation,
  GbgAddressValidationDetails,
} from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/types';
import Tooltip from '~/components/core/Atomic/Tooltip';
import type { StatusChipProps } from '~/components/core/StatusChipCore';
import StatusChipCore from '~/components/core/StatusChipCore';
import Text from '~/components/core/TextComponents/Text';
import CheckIcon from '~/components/icons/CheckIcon';
import WarningCircleIcon from '~/components/icons/WarningCircle';
import { serverDateToLocal } from '~/DateTimeUtils';
import type { ContactInfoContactValidationInfo } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

const TEXTS = {
  verified: 'VERIFIED',
  verifiedCorrected: 'VERIFIED - CORRECTED',
  manualOverride: 'MANUAL OVERRIDE',
  notValidated: 'NOT VALIDATED',
  verifiedOn: 'Verified on',
  overrideReason: 'Reason',
  overrideBy: 'By',
  overrideDate: 'Date',
} as const;

function getAddressValidation(
  validations?: ContactInfoContactValidationInfo[]
): ContactInfoContactValidationInfo | undefined {
  return validations?.find((v) => v.validation_type === ADDRESS_VALIDATION_TYPE);
}

const getBadgeConfig = (status: string): StatusChipProps => {
  switch (status) {
    case ADDRESS_VALIDATION_STATUSES.VERIFIED:
      return { status: TEXTS.verified, color: 'green', Icon: CheckIcon };
    case ADDRESS_VALIDATION_STATUSES.VERIFIED_CORRECTED:
      return { status: TEXTS.verifiedCorrected, color: 'green', Icon: CheckIcon };
    case ADDRESS_VALIDATION_STATUSES.MANUAL_OVERRIDE:
      return { status: TEXTS.manualOverride, color: 'orange', Icon: CheckIcon };
    case ADDRESS_VALIDATION_STATUSES.NOT_VALIDATED:
    default:
      return { status: TEXTS.notValidated, color: 'grey', Icon: WarningCircleIcon };
  }
};

interface VerifiedTooltipContentProps {
  details: GbgAddressValidationDetails;
  formattedDate?: string;
}

const TooltipRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <div>
      <span className="font-semibold">{label}:</span> {value}
    </div>
  );
};

const VerifiedTooltipContent: React.FC<VerifiedTooltipContentProps> = ({ details, formattedDate }) => (
  <Text
    className="p-4"
    variant={Text.VARIANTS.XS}
    weight={Text.WEIGHTS.REGULAR}
    colorVariant={Text.COLOR_VARIANTS.WHITE}
  >
    {details.ref_type && details.ref_id ? <TooltipRow label={details.ref_type} value={details.ref_id} /> : null}
    {formattedDate ? <TooltipRow label={TEXTS.verifiedOn} value={formattedDate} /> : null}
  </Text>
);

interface OverrideTooltipContentProps {
  details: GbgAddressValidationDetails;
}

const OverrideTooltipContent: React.FC<OverrideTooltipContentProps> = ({ details }) => (
  <Text
    className="p-4"
    variant={Text.VARIANTS.XS}
    weight={Text.WEIGHTS.REGULAR}
    colorVariant={Text.COLOR_VARIANTS.WHITE}
  >
    {details.override_reason ? <TooltipRow label={TEXTS.overrideReason} value={details.override_reason} /> : null}
    {details.override_user_name ? <TooltipRow label={TEXTS.overrideBy} value={details.override_user_name} /> : null}
    {details.override_timestamp ? (
      <TooltipRow label={TEXTS.overrideDate} value={serverDateToLocal(details.override_timestamp)} />
    ) : null}
  </Text>
);

interface AddressVerificationBadgeProps {
  validations?: ContactInfoContactValidationInfo[];
  sessionValidation?: GbgAddressRetrieveValidation;
  className?: string;
}

const AddressVerificationBadge: React.FC<AddressVerificationBadgeProps> = ({
  validations,
  sessionValidation,
  className,
}) => {
  const dbValidation = getAddressValidation(validations);
  const effectiveStatus =
    sessionValidation?.status ?? dbValidation?.status ?? ADDRESS_VALIDATION_STATUSES.NOT_VALIDATED;
  const badgeConfig = getBadgeConfig(effectiveStatus);

  const sessionDetails =
    sessionValidation?.validation_details?.type === ADDRESS_VALIDATION_TYPE
      ? sessionValidation.validation_details
      : null;
  const dbDetails =
    dbValidation?.validation_details?.type === ADDRESS_VALIDATION_TYPE ? dbValidation.validation_details : null;
  const details = sessionDetails ?? dbDetails;
  const formattedDate = !sessionValidation && dbValidation ? serverDateToLocal(dbValidation.updated_at) : undefined;

  const mainBadge = (
    <div data-testid="address-verification-badge" className="inline-block">
      <StatusChipCore color={badgeConfig.color} status={badgeConfig.status} Icon={badgeConfig.Icon} />
    </div>
  );

  const isVerifiedStatus =
    effectiveStatus === ADDRESS_VALIDATION_STATUSES.VERIFIED ||
    effectiveStatus === ADDRESS_VALIDATION_STATUSES.VERIFIED_CORRECTED;

  let chip = mainBadge;
  if (details && isVerifiedStatus) {
    chip = (
      <Tooltip
        title={<VerifiedTooltipContent details={details} formattedDate={formattedDate} />}
        enterDelay={400}
        enterNextDelay={400}
      >
        {mainBadge}
      </Tooltip>
    );
  } else if (details && effectiveStatus === ADDRESS_VALIDATION_STATUSES.MANUAL_OVERRIDE) {
    chip = (
      <Tooltip title={<OverrideTooltipContent details={details} />} enterDelay={400} enterNextDelay={400}>
        {mainBadge}
      </Tooltip>
    );
  }

  return <div className={cn('-ms-4', className)}>{chip}</div>;
};

export default AddressVerificationBadge;
